"use client";

import * as React from "react";
import confetti from "canvas-confetti";
import Image from "next/image";
import { Mail, Phone, MessageSquare, ShieldAlert, Award, ArrowRight, RotateCcw, Clock, CheckCircle2, MapPin } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { CopyButton } from "@/components/ui/CopyButton";
import { FormSuccess } from "@/components/ui/FormSuccess";
import { ErrorState } from "@/components/ui/ErrorState";
import { useAutosaveForm } from "@/hooks/useAutosaveForm";
import { globalToast } from "@/components/ui/ToastProvider";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal";

import { getStoredAttribution } from "@/components/analytics/UTMTracker";

export default function ContactContent() {
  const initialForm = {
    name: "",
    contact: "",
    softwareNeeded: "",
  };

  const {
    formData,
    setFormData,
    lastSaved,
    hasDraft,
    clearDraft,
    markSubmitted,
  } = useAutosaveForm({
    formKey: "contact_inquiry",
    initialValues: initialForm,
  });

  const [status, setStatus] = React.useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedbackMsg, setFeedbackMsg] = React.useState("");
  const [submittedRef, setSubmittedRef] = React.useState("");
  const [showClearModal, setShowClearModal] = React.useState(false);

  // Pre-fill "Software Needed" from URL parameters if arriving from a specific service/page link
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const prefill = params.get("software") || params.get("subject") || params.get("service");
      if (prefill) {
        setFormData((prev) => ({
          ...prev,
          softwareNeeded: prefill.replace(/\+/g, " "),
        }));
      }
    }
  }, [setFormData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setFeedbackMsg("");

    try {
      const attribution = getStoredAttribution();
      const rawName = (formData.name || "").trim();
      const rawContact = (formData.contact || "").trim();
      const rawSoftware = (formData.softwareNeeded || "").trim();
      const isEmail = rawContact.includes("@");

      if (!rawName || rawName.length < 2) {
        setStatus("error");
        setFeedbackMsg("Please enter your name (at least 2 characters).");
        return;
      }

      if (!rawContact || rawContact.length < 5) {
        setStatus("error");
        setFeedbackMsg("Please enter a valid email or phone number.");
        return;
      }

      const payload = {
        name: rawName,
        email: isEmail
          ? rawContact.toLowerCase()
          : `${rawContact.replace(/[^\d+]/g, "").replace("+", "") || "lead"}@lead.johnsonsoftwares.com`,
        phone: !isEmail ? rawContact : undefined,
        contact: rawContact,
        service_type: rawSoftware || "Custom Software Development",
        software_needed: rawSoftware,
        project_details: `Inquiry / Software Needed: ${rawSoftware}`,
        landing_page: "/contact",
        ...attribution,
      };

      const response = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (response.ok && data?.success) {
        setStatus("success");
        setFeedbackMsg(data.message || "Thank you! We have received your inquiry.");
        
        // Trigger premium Confetti explosion
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
        });

        // Reset form
        setFormData({
          name: "",
          contact: "",
          softwareNeeded: "",
        });

        // Safe reference ID (leadId can be integer or string; safely convert to string)
        const refId = data.leadId 
          ? `REF-${String(data.leadId).padStart(4, "0").toUpperCase()}` 
          : `REF-${Date.now().toString().slice(-6)}`;
        setSubmittedRef(refId);
        markSubmitted();

        globalToast.success("Inquiry received! Our team will connect with you within 24 hours.");

        // Smooth redirect to dedicated conversion page for Google Ads & Analytics tracking
        setTimeout(() => {
          window.location.href = "/thank-you";
        }, 1200);
      } else {
        setStatus("error");
        setFeedbackMsg(data?.error || `Submission error (${response.status}). Please try again.`);
      }
    } catch (err: any) {
      console.error("[Contact Form] Submit error:", err);
      setStatus("error");
      setFeedbackMsg(err?.message || "Network error. Please try again later.");
    }
  };


  return (
    <div className="w-full flex-grow py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden">
      {/* Background Mesh */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="bg-mesh" />
      </div>

      <div className="mx-auto max-w-7xl relative z-10 space-y-12">
        {/* Architectural Hero Header */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/20 text-[#2563EB] text-xs font-mono">
            <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse" />
            EXECUTIVE CONSULTATION // DIRECT PRINCIPAL ACCESS
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-foreground tracking-tight leading-[1.08]">
                Technical Scoping &amp; <br />
                <span className="text-[#2563EB]">Architectural Consultation.</span>
              </h1>
              <p className="text-base sm:text-lg text-text-muted mt-3 max-w-2xl">
                Direct engagement with principal software architects. Zero sales fluff, guaranteed NDA protection, and deterministic timelines.
              </p>
            </div>

            <div className="flex items-center gap-4 text-xs font-mono text-text-muted shrink-0 pb-1">
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#2563EB]" /> &lt;24h Turnaround</span>
              <span className="text-foreground/20">•</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> NDA Assured</span>
            </div>
          </div>

          {/* Architectural Office Banner */}
          <div className="relative rounded-2xl overflow-hidden border border-[#111318]/10 h-64 sm:h-80 lg:h-96 shadow-sm">
            <Image
              src="/images/storytelling/office/meeting-room.webp"
              alt="Johnson Softwares Executive Scoping Room"
              fill
              priority
              className="object-cover object-center"
              sizes="(max-width: 1280px) 100vw, 1280px"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111318]/80 via-[#111318]/20 to-transparent" />
            
            <div className="absolute bottom-6 left-6 right-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4 text-white">
              <div>
                <span className="px-2.5 py-1 rounded bg-white/20 backdrop-blur-md text-[11px] font-mono tracking-wider uppercase">
                  CONFIDENTIAL SCOPING ROOM // NDA PROTECTED
                </span>
                <p className="text-lg sm:text-xl font-bold mt-2 font-heading">
                  Architecture &amp; Commercial Discovery Session
                </p>
                <p className="text-xs text-white/80 max-w-md mt-0.5">
                  Discuss database topology, bespoke CRM flows, cloud economics, or AI automations directly with leadership.
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 bg-white/10 backdrop-blur-md px-3.5 py-2 rounded-xl border border-white/15 text-xs font-mono">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                DIRECT ACCESS GUARANTEED
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Direct channels and trust assurances */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-[#2563EB] font-bold">
                Direct Communication Channels
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-extrabold text-foreground mt-2 leading-tight">
                Immediate Engineer Access
              </h2>
              <p className="text-sm text-text-muted mt-2">
                Pick your preferred communication channel below. Principal architects review every incoming scope within 24 hours.
              </p>
            </div>

            {/* Direct Cards */}
            <div className="flex flex-col gap-4">
              {/* WhatsApp card */}
              <a
                href="https://wa.me/919880893416"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-5 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl hover:bg-emerald-500/15 transition-all group cursor-pointer"
              >
                <div className="flex gap-4 items-center">
                  <span className="p-3 bg-emerald-600 text-white rounded-xl shadow-xs">
                    <MessageSquare className="h-5 w-5" />
                  </span>
                  <div>
                    <span className="block font-bold text-foreground text-sm font-heading">Instant WhatsApp Chat</span>
                    <span className="block text-xs text-text-muted">Direct line to senior technical architect.</span>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-emerald-600 group-hover:translate-x-1 transition-transform" />
              </a>

              {/* Email Copier */}
              <div className="flex items-center justify-between p-5 bg-white dark:bg-card border border-[#111318]/10 rounded-2xl shadow-xs">
                <div className="flex gap-4 items-center min-w-0">
                  <span className="p-3 bg-[#2563EB]/10 rounded-xl text-[#2563EB] shrink-0">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <span className="block font-bold text-foreground text-sm font-heading">Direct RFP &amp; Inquiries</span>
                    <span className="block text-xs text-text-muted truncate font-mono">contact@johnsonsoftwares.com</span>
                  </div>
                </div>
                <CopyButton text="contact@johnsonsoftwares.com" label="Copy" variant="pill" />
              </div>

              {/* Direct call */}
              <div className="flex items-center justify-between p-5 bg-white dark:bg-card border border-[#111318]/10 rounded-2xl shadow-xs">
                <a href="tel:+919880893416" className="flex gap-4 items-center flex-1 hover:opacity-80 transition-opacity">
                  <span className="p-3 bg-[#2563EB]/10 rounded-xl text-[#2563EB] shrink-0">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <span className="block font-bold text-foreground text-sm font-heading">Direct Phone Line</span>
                    <span className="block text-xs text-text-muted font-mono">+91 98808 93416</span>
                  </div>
                </a>
                <CopyButton text="+919880893416" label="Call" variant="pill" />
              </div>

              {/* Physical Office Address */}
              <div className="flex items-center justify-between p-5 bg-white dark:bg-card border border-[#111318]/10 rounded-2xl shadow-xs">
                <div className="flex gap-4 items-center min-w-0">
                  <span className="p-3 bg-[#2563EB]/10 rounded-xl text-[#2563EB] shrink-0">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <span className="block font-bold text-foreground text-sm font-heading">Bengaluru Engineering Office</span>
                    <span className="block text-xs text-text-muted leading-relaxed">
                      27th Main Road, HSR Layout, Bangalore 560102, Karnataka, India
                    </span>
                  </div>
                </div>
                <CopyButton text="27th Main Road, HSR Layout, Bangalore 560102, Karnataka, India" label="Copy" variant="pill" />
              </div>
            </div>

            {/* Trust Assurances block */}
            <div className="p-6 bg-white/70 dark:bg-card/70 rounded-2xl border border-[#111318]/10 space-y-4 shadow-xs">
              <div className="flex gap-3 items-start">
                <ShieldAlert className="h-5 w-5 text-[#2563EB] shrink-0" />
                <p className="text-xs text-text-muted leading-relaxed">
                  <strong>NDA Assured</strong>: We sign Non-Disclosure Agreements before starting scoping sessions. Your operational models are fully protected.
                </p>
              </div>
              <div className="flex gap-3 items-start">
                <Award className="h-5 w-5 text-primary dark:text-accent shrink-0" />
                <p className="text-xs text-text-muted leading-relaxed">
                  <strong>Flat Estimates</strong>: All project templates contain clear pricing maps to guarantee transparent scoping without pricing creep.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Simplified Inbound Inquiry Form (Name, Email/Phone, Software Needed) */}
          <div className="lg:col-span-7">
            <Card className="p-6 sm:p-10" glowColor="rgba(37, 99, 235, 0.15)">
              <h2 className="font-heading font-extrabold text-2xl text-foreground mb-2">
                Request a 10-Minute Demo or Free Quote
              </h2>
              <p className="text-xs text-text-muted mb-8 leading-relaxed">
                Fill in the 3 quick details below and our engineering team will get in touch directly within 24 hours.
              </p>

              {status === "success" ? (
                <FormSuccess
                  referenceId={submittedRef}
                  onReset={() => {
                    setStatus("idle");
                    clearDraft();
                  }}
                />
              ) : status === "error" ? (
                <ErrorState
                  message={feedbackMsg}
                  onRetry={() => setStatus("idle")}
                />
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Autosave Draft Indicator Banner */}
                  {hasDraft && lastSaved && (
                    <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 text-xs">
                      <span className="text-blue-700 dark:text-blue-300 flex items-center gap-1.5 font-medium">
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        Draft restored ({lastSaved.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowClearModal(true)}
                        className="text-[11px] text-slate-500 hover:text-rose-500 hover:underline cursor-pointer flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" />
                        <span>Clear draft</span>
                      </button>
                    </div>
                  )}

                  {/* 1. Name */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-name" className="text-xs font-bold text-foreground uppercase tracking-wider">
                      Your Name *
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      placeholder="e.g. Vikram Singh"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-border-muted rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground"
                    />
                  </div>

                  {/* 2. Email / Phone */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-info" className="text-xs font-bold text-foreground uppercase tracking-wider">
                      Email / Phone *
                    </label>
                    <input
                      id="contact-info"
                      type="text"
                      required
                      placeholder="e.g. name@company.com or +91 98808 93416"
                      value={formData.contact}
                      onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                      className="px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-border-muted rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground"
                    />
                    <span className="text-[11px] text-text-muted">
                      Enter either your business email or WhatsApp/phone number.
                    </span>
                  </div>

                  {/* 3. Software Needed */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="software-needed" className="text-xs font-bold text-foreground uppercase tracking-wider">
                      Software Needed *
                    </label>
                    <input
                      id="software-needed"
                      list="software-suggestions"
                      type="text"
                      required
                      placeholder="e.g. Restaurant Management Software, Pharmacy POS, Custom CRM..."
                      value={formData.softwareNeeded}
                      onChange={(e) => setFormData({ ...formData, softwareNeeded: e.target.value })}
                      className="px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-border-muted rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm text-foreground"
                    />
                    <datalist id="software-suggestions">
                      <option value="Restaurant Management Software & Cloud POS" />
                      <option value="Wholesale Pharmacy & Pharma Software" />
                      <option value="Custom CRM & WhatsApp Lead Pipeline" />
                      <option value="Real Estate CRM & Booking Engine" />
                      <option value="Enterprise ERP & Inventory System" />
                      <option value="AI Automation & Sales Agents" />
                      <option value="Custom Mobile / Web Application" />
                    </datalist>

                    {/* Quick-select suggestion chips */}
                    <div className="flex flex-wrap gap-2 pt-2">
                      {[
                        "Restaurant Management Software",
                        "Pharmacy Software",
                        "Custom CRM Software",
                        "AI Automation & Agents",
                      ].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setFormData({ ...formData, softwareNeeded: preset })}
                          className={`text-xs px-3 py-1 rounded-full border transition-all cursor-pointer ${
                            formData.softwareNeeded === preset
                              ? "bg-primary text-white border-primary shadow-xs"
                              : "bg-slate-100 dark:bg-slate-800 text-text-muted hover:text-foreground border-border-muted/60"
                          }`}
                        >
                          + {preset}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    className="w-full justify-center text-base font-bold py-4 mt-2"
                    isLoading={status === "submitting"}
                  >
                    Request a 10-Minute Demo / Get a Free Quote
                  </Button>
                </form>
              )}
            </Card>
          </div>

        </div>
      </div>

      {/* Draft Reset Confirmation Modal */}
      <ConfirmationModal
        isOpen={showClearModal}
        onClose={() => setShowClearModal(false)}
        onConfirm={() => {
          const prev = { ...formData };
          clearDraft();
          setShowClearModal(false);
          globalToast.info("Form draft cleared", {
            undo: () => {
              setFormData(prev);
              globalToast.success("Form draft restored");
            },
            undoLabel: "Undo",
          });
        }}
        title="Clear Saved Draft?"
        description="Are you sure you want to discard your saved draft? All filled inputs will be reset to blank."
        confirmLabel="Discard Draft"
        cancelLabel="Keep Draft"
        variant="danger"
      />
    </div>
  );
}
